import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TextBlock } from './imageTranslatorTypes.types';
import ImageCanvas from './ImageCanvas';
import { ImageCanvasHandle } from './ImageCanvas';
import { EditableTextOptions } from './EditableTextOptions';
import { urlToBase64 } from '../../utils/images';
import { MdClose, MdAdd } from 'react-icons/md';
import { ImageData } from './imageTranslatorTypes.types';
import { detectText, translateText } from './imageTranslationAPIs';
import { SUPPORTED_LANGUAGES } from './imageTranslatorConstants';


const updateBlock = (
  images: ImageData[],
  imageId: string,
  blockId: string,
  updates: Partial<TextBlock>
): ImageData[] => {
  return images.map(img => 
    img.id === imageId 
      ? { ...img, textBlocks: img.textBlocks.map(block => block.id === blockId ? { ...block, ...updates } : block) }
      : img
  );
};


// Add these props to the existing props
interface ImageTranslatorProps {
  initialFiles: File[];
  existingImages: Array<{ url: string; id: string; editorData?: any }>;
  onFinish: (images: Array<{ serverId?: string; dataUrl: string; editorData?: any }>) => Promise<void>;
  onClose: () => void;
}

function ImageTranslator({ onFinish, initialFiles, existingImages, onClose }: ImageTranslatorProps) {
  const [images, setImages] = useState<ImageData[]>([]);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [selectedBlockIds, setSelectedBlockIds] = useState<Set<string>>(new Set());
  const [clipboardBlocks, setClipboardBlocks] = useState<TextBlock[] | null>(null);
  const selectedImage = useMemo(() => 
    images.find(img => img.id === selectedImageId),
    [images, selectedImageId]
  );
  const selectedBlocks = useMemo(() => 
    selectedImage?.textBlocks.filter(b => selectedBlockIds.has(b.id)) || [],
    [selectedImage, selectedBlockIds]
  );

  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [targetLanguage, setTargetLanguage] = useState('fr');
  const [isLoading, setIsLoading] = useState(false);

  const imageCanvasRef = useRef<ImageCanvasHandle>(null);
  const [optionsPanelPosition, setOptionsPanelPosition] = useState(() => {
    const saved = localStorage.getItem('optionsPanelPosition');
    return saved ? JSON.parse(saved) : { x: 20, y: 20 };
  });
  const [colorPickerCallback, setColorPickerCallback] = useState<((color: string) => void) | null>(null);


  const handleImageUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setIsLoading(true);
    try {
      const newImages: ImageData[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const imageUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.readAsDataURL(file);
        });

        const blocks = await detectText(imageUrl);
        
        newImages.push({
          id: crypto.randomUUID(),
          url: imageUrl,
          textBlocks: blocks,
          finished: false
        });
      }

      setImages(prev => [...prev, ...newImages]);
      if (!selectedImageId && newImages.length > 0) {
        setSelectedImageId(newImages[0].id);
      }
    } catch (error) {
      console.error('Error processing images:', error);
      alert('Failed to process images. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedImageId]);

  const handleImageSelect = useCallback((imageId: string) => {
    setSelectedImageId(imageId);
    setSelectedBlockIds(new Set());
  }, [images]);

  const handleTranslate = useCallback(async () => {
    if (!selectedImage) return;
    
    setIsLoading(true);
    try {
      const textsToTranslate = selectedImage.textBlocks.map(block => block.text);
      const translatedTexts = await translateText(
        textsToTranslate,
        sourceLanguage,
        targetLanguage
      );
      
      const translated = selectedImage.textBlocks.map((block, index) => ({
        ...block,
        originalText: block.text,
        text: translatedTexts[index]
      }));

      setImages(prev => prev.map(img => 
        img.id === selectedImageId 
          ? { ...img, textBlocks: translated }
          : img
      ));
    } catch (error) {
      console.error('Translation error:', error);
      alert('Failed to translate texts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedImage, selectedImageId, sourceLanguage, targetLanguage]);

  const handleBlockSelect = useCallback((blockId: string | null, ctrlKey: boolean) => {
    setSelectedBlockIds(prev => {
      const newSelection = new Set(prev);
      
      if (!blockId) {
        newSelection.clear();
        return newSelection;
      }
      
      if (ctrlKey) {
        // Toggle selection when ctrl is pressed
        if (newSelection.has(blockId)) {
          newSelection.delete(blockId);
        } else {
          newSelection.add(blockId);
        }
      } else {
        // Single selection when ctrl is not pressed
        newSelection.clear();
        newSelection.add(blockId);
      }
      
      return newSelection;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!selectedImage) return;
    
    setSelectedBlockIds(new Set(selectedImage.textBlocks.map(block => block.id)));
  }, [selectedImage]);

  const handleSelectedTextBlocksUpdate = useCallback((updates: Partial<TextBlock>) => {
    if (!selectedImage || selectedBlockIds.size === 0) return;

    setImages(prev => prev.map(img => {
      if (img.id !== selectedImage.id) return img;
      
      return {
        ...img,
        textBlocks: img.textBlocks.map(block => 
          selectedBlockIds.has(block.id) 
            ? { ...block, ...updates }
            : block
        )
      };
    }));
  }, [selectedImage, selectedBlockIds]);

  const handleTextBlockChange = useCallback((blockId: string, newBlockAttrs: Partial<TextBlock>) => {
    if (!selectedImageId || !selectedImage) return;

    
    setImages(prev => updateBlock(
      prev,
      selectedImageId,
      blockId,
      newBlockAttrs
    ));
  }, [selectedImageId, selectedImage]);


  const handlePickColor = (callback: (color: string) => void) => {
    setColorPickerCallback(() => callback);
  };

  const handleColorPicked = useCallback((color: string) => {
    if (colorPickerCallback) {
      colorPickerCallback(color);
      setColorPickerCallback(null);
    }
  }, [colorPickerCallback]);



  const handleTextBlockDelete = useCallback((id: string) => {
    if (!selectedImageId || !selectedImage) return;
    
    setImages(prev => prev.map(img => 
      img.id === selectedImageId 
        ? { 
            ...img, 
            textBlocks: img.textBlocks.filter(b => b.id !== id)
          }
        : img
    ));
    setSelectedBlockIds(prev => {
      const newSelection = new Set(prev);
      newSelection.delete(id);
      return newSelection;
    });
  }, [selectedImageId, selectedImage]);


  

  const handleSourceLanguageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSourceLanguage(e.target.value);
  }, []);

  const handleTargetLanguageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setTargetLanguage(e.target.value);
  }, []);


  const handleAddNewText = useCallback(() => {
    if (!selectedImageId) return;

    const newBlock: TextBlock = {
      id: crypto.randomUUID(),
      text: 'New Text',
      x: 50,
      y: 50,
      width: 200,
      height: 50,
      fontSize: 24,
      fontFamily: 'Arial',
      fontWeight: 'normal',
      fontStyle: 'normal',
      color: '#202020',
      textOpacity: 1,
      backgroundColor: '#ffffff',
      backgroundOpacity: 1,
      padding: 4,
      cornerRadius: 6,
      rotation: 0,
    };

    setImages(prev => prev.map(img => {
      if (img.id === selectedImageId) {
        return {
          ...img,
          textBlocks: [...img.textBlocks, newBlock],
        };
      }
      return img;
    }));

    // Select the new block
    setSelectedBlockIds(new Set([newBlock.id]));
  }, [selectedImageId]);

  const handleRemoveImage = useCallback((imageId: string) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
    if (selectedImageId === imageId) {
      setSelectedImageId(null);
      setSelectedBlockIds(new Set());
    }
  }, [selectedImageId]);

  const handleMarkAsFinished = useCallback((imageId: string, finished: boolean) => {
    setImages(prev => prev.map(img => 
      img.id === imageId 
        ? { ...img, finished }
        : img
    ));
    
    // Clear selection when marking as finished
    if (finished) {
      setSelectedBlockIds(new Set());
    }
  }, []);

  const handleDownloadAllFinished = useCallback(async () => {
    if (!imageCanvasRef.current) return;

    const finishedImages = images.filter(img => img.finished);
    if (finishedImages.length === 0) {
      alert('No finished images to download');
      return;
    }

    const translatedImageUrls = [];
    
    for (const image of finishedImages) {
      setSelectedImageId(image.id);
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const dataUrl = await imageCanvasRef.current.downloadCanvas();
      translatedImageUrls.push({
        dataUrl,
        serverId: image.serverId, // Pass along the server ID if it exists
        editorData: image.textBlocks // Pass the editor data (text blocks)
      });
    }
    
    if (onFinish) {
      onFinish(translatedImageUrls);
    }
  }, [images, imageCanvasRef, onFinish]);

  const handleOptionsPanelDrag = useCallback((newPosition: { x: number; y: number }) => {
    setOptionsPanelPosition(newPosition);
    localStorage.setItem('optionsPanelPosition', JSON.stringify(newPosition));
  }, []);

  // Add this function to get the next block ID
  const getNextBlockId = useCallback((currentId: string | null) => {
    if (!selectedImage?.textBlocks.length) return null;
    
    if (!currentId) return selectedImage.textBlocks[0].id;
    
    const currentIndex = selectedImage.textBlocks.findIndex(block => block.id === currentId);
    if (currentIndex === -1) return selectedImage.textBlocks[0].id;
    
    // Get next index, wrapping around to 0 if at the end
    const nextIndex = (currentIndex + 1) % selectedImage.textBlocks.length;
    return selectedImage.textBlocks[nextIndex].id;
  }, [selectedImage]);

  const getPrevBlockId = useCallback((currentId: string | null) => {
    if (!currentId || !selectedImage) return null;
    
    const currentIndex = selectedImage.textBlocks.findIndex(block => block.id === currentId);
    if (currentIndex === -1 || currentIndex === 0) return selectedImage.textBlocks[selectedImage.textBlocks.length - 1].id;

    return selectedImage.textBlocks[currentIndex - 1].id;
  }, [selectedImage]);

  // Add keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle tab if we're in translation view and not finished
      if (!selectedImage || selectedImage.finished) return;
      
      if (e.key === 'Tab') {
        e.preventDefault(); // Prevent default tab behavior
        
        if (e.shiftKey) {
          // Select previous block
          const prevId = getPrevBlockId(selectedBlockIds.size > 0 ? selectedBlocks[0].id : null);
          handleBlockSelect(prevId, false);
        } else {
        // Select next block
          const nextId = getNextBlockId(selectedBlockIds.size > 0 ? selectedBlocks[0].id : null);
          handleBlockSelect(nextId, false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, selectedBlockIds, getNextBlockId, handleBlockSelect]);

  // Add keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle 'n' key if not editing text and not in an input field
      if (e.key.toLowerCase() === 'n'
      && selectedImageId
      && document.activeElement?.tagName !== 'INPUT' 
      && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        
        // Find current image index
        const currentIndex = images.findIndex(img => img.id === selectedImageId);
        if (currentIndex === -1) return;

        // Mark current image as finished
        handleMarkAsFinished(selectedImageId, true);

        // Move to next image if available
        const nextIndex = currentIndex + 1;
        if (nextIndex < images.length) {
          setSelectedImageId(images[nextIndex].id);
          setSelectedBlockIds(new Set());
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [images, selectedImageId, handleMarkAsFinished]);

  // Add effect to handle existing images
  useEffect(() => {
    if (existingImages?.length) {
      const loadExistingImages = async () => {
        setIsLoading(true);
        try {
          const newImages: ImageData[] = [];

          for (const image of existingImages) {
            // Convert web URL to base64
            const base64Url = await urlToBase64(image.url);
            
            // Use existing editor data if available, otherwise detect text
            let blocks;
            if (image.editorData && Array.isArray(image.editorData)) {
              blocks = image.editorData;
            } else {
              blocks = await detectText(base64Url);
            }
            
            newImages.push({
              id: crypto.randomUUID(),
              url: base64Url,
              serverId: image.id, // Store the server ID
              textBlocks: blocks,
              finished: false
            });
          }

          setImages(prev => [...prev, ...newImages]);
          if (!selectedImageId && newImages.length > 0) {
            setSelectedImageId(newImages[0].id);
          }
        } catch (error) {
          console.error('Error processing existing images:', error);
          throw error;
        } finally {
          setIsLoading(false);
        }
      };

      loadExistingImages();
    }
  }, []);

  // Add effect to handle initial files
  useEffect(() => {
    if (initialFiles?.length) {
      const processInitialFiles = async () => {
        setIsLoading(true);
        try {
          const newImages: ImageData[] = [];

          for (const file of initialFiles) {
            const imageUrl = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target?.result as string);
              reader.readAsDataURL(file);
            });

            const blocks = await detectText(imageUrl);
            
            newImages.push({
              id: crypto.randomUUID(),
              url: imageUrl,
              textBlocks: blocks,
              finished: false
            });
          }

          setImages(prev => [...prev, ...newImages]);
          if (!selectedImageId && newImages.length > 0) {
            setSelectedImageId(newImages[0].id);
          }
        } catch (error) {
          console.error('Error processing initial files:', error);
        } finally {
          setIsLoading(false);
        }
      };

      processInitialFiles();
    }
  }, []);


  const handleDeleteSelectedTextBlock = useCallback(() => {
    setImages(prev => prev.map(img => 
      img.id === selectedImageId
        ? {
            ...img,
            textBlocks: img.textBlocks.filter(b => !selectedBlockIds.has(b.id))
          }
        : img
    ));
  }, [selectedImageId, selectedBlockIds]);


  // Update the UI to show a different message when there are no text blocks
  const getFinishButtonText = useCallback(() => {
    if (selectedImage?.finished) return 'Keep Editing';
    return 'Mark as Finished!';
  }, [selectedImage]);

  // Add function to get common values across selected blocks
  const getCommonValues = useCallback(() => {
    if (!selectedBlocks.length) return null;
    
    const firstBlock = selectedBlocks[0];
    const commonValues: Partial<TextBlock> = {};
    
    // List of properties to check
    const propertiesToCheck: (keyof TextBlock)[] = [
      'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'color',
      'textOpacity', 'backgroundColor', 'backgroundOpacity', 'padding',
      'cornerRadius', 'align', 'verticalAlign', 'lineHeight'
    ];

    propertiesToCheck.forEach(prop => {
      const value = firstBlock[prop];
      const allSame = selectedBlocks.every(block => block[prop] === value);
      if (allSame) {
        commonValues[prop] = value;
      }
    });

    // Check nested properties
    ['textBorder', 'boxShadow', 'backgroundBorder'].forEach(prop => {
      const firstValue = firstBlock[prop as keyof TextBlock];
      const allSame = selectedBlocks.every(block => 
        JSON.stringify(block[prop as keyof TextBlock]) === JSON.stringify(firstValue)
      );
      if (allSame) {
        commonValues[prop as keyof TextBlock] = firstValue;
      }
    });

    return commonValues;
  }, [selectedBlocks]);

  const handleCopyTextBlocks = useCallback(() => {
    if (!selectedImage) return;
    
    // If blocks are selected, copy only those blocks
    if (selectedBlockIds.size > 0) {
      const blocksToCopy = selectedImage.textBlocks.filter(block => selectedBlockIds.has(block.id));
      setClipboardBlocks(blocksToCopy);
    } else {
      // Otherwise, copy all blocks
      setClipboardBlocks([...selectedImage.textBlocks]);
    }
    
    // Show confirmation toast or message
    alert('Text blocks copied! You can now paste them to another image.');
  }, [selectedImage, selectedBlockIds]);

  const handlePasteTextBlocks = useCallback(() => {
    if (!selectedImageId || !clipboardBlocks || clipboardBlocks.length === 0) return;
    
    // Create new blocks with new IDs
    const newBlocks = clipboardBlocks.map(block => ({
      ...block,
      id: crypto.randomUUID() // Generate new IDs for the pasted blocks
    }));
    
    // Add blocks to the current image
    setImages(prev => prev.map(img => {
      if (img.id === selectedImageId) {
        return {
          ...img,
          textBlocks: [...img.textBlocks, ...newBlocks]
        };
      }
      return img;
    }));
    
    // Select the newly pasted blocks
    setSelectedBlockIds(new Set(newBlocks.map(block => block.id)));
  }, [selectedImageId, clipboardBlocks]);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between py-4 px-8">
          <div className="flex gap-2 overflow-x-auto">
            {images.map((img) => (
              <div
                key={img.id}
                className={`relative group cursor-pointer ${
                  img.id === selectedImageId ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => handleImageSelect(img.id)}
              >
                <img
                  src={img.url}
                  alt="Uploaded"
                  className="h-20 w-auto object-contain"
                />
                {img.finished && (
                  <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(img.id);
                  }}
                  className="absolute top-1 left-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove image"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          
          <button
            onClick={handleDownloadAllFinished}
            className="px-4 py-2 bg-green-500 text-white rounded-sm hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={!images.some(img => img.finished)}
          >
            <span>Finished </span>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-8">
        {images.length === 0 && (
          <ImageTranslatorEmpty
            isLoading={isLoading}
            handleImageUpload={handleImageUpload}
          />
        )}
        
        {selectedImage && selectedImageId && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-between items-center mb-4">
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to close the editor?')) {
                      onClose();
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 flex items-center gap-2"
                >
                  <MdClose size={20} />
                  Close Editor
                </button>
                <div className="flex gap-4">
                    <div className="flex items-center gap-4">
                      <select
                        value={sourceLanguage}
                        onChange={handleSourceLanguageChange}
                        className="border rounded-sm px-3 py-2"
                        disabled={isLoading}
                      >
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                      <select
                        value={targetLanguage}
                        onChange={handleTargetLanguageChange}
                        className="border rounded-sm px-3 py-2"
                        disabled={isLoading}
                      >
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleTranslate}
                        className="bg-blue-500 text-white px-4 py-2 rounded-sm hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={selectedImage.textBlocks.length === 0 || isLoading}
                      >
                        {isLoading ? 'Translating...' : 'Translate'}
                      </button>
                    </div>
                  <button
                    onClick={handleAddNewText}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    <MdAdd size={20} />
                    New Text
                  </button>
                  {!selectedImage.finished && (
                    <button
                      onClick={() => handleMarkAsFinished(selectedImageId, true)}
                      className="bg-green-500 text-white px-4 py-2 rounded-sm hover:bg-green-600"
                    >
                      {getFinishButtonText()}
                    </button>
                  )}
                  {selectedImage.finished && (
                    <button
                      onClick={() => handleMarkAsFinished(selectedImageId, false)}
                      className="bg-blue-500 text-white px-4 py-2 rounded-sm hover:bg-blue-600"
                    >
                      Keep Editing
                    </button>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSelectAll}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h16M4 12h16m-7 7h7" />
                    </svg>
                    Select All Text Blocks
                  </button>
                  <button
                    onClick={handleCopyTextBlocks}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-sm flex items-center gap-2"
                    title={selectedBlockIds.size > 0 ? "Copy selected blocks" : "Copy all blocks"}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                    {selectedBlockIds.size > 0 ? "Copy Selected" : "Copy All"}
                  </button>
                  {clipboardBlocks && clipboardBlocks.length > 0 && (
                    <button
                      onClick={handlePasteTextBlocks}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-sm flex items-center gap-2"
                      title={`Paste ${clipboardBlocks.length} blocks`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      Paste ({clipboardBlocks.length})
                    </button>
                  )}
                </div>
              </div>

              <ImageCanvas
                ref={imageCanvasRef}
                imageUrl={selectedImage.url}
                textBlocks={selectedImage.textBlocks}
                onTextBlockChange={handleTextBlockChange}
                onTextBlockDelete={handleTextBlockDelete}
                onSelectBlock={handleBlockSelect}
                selectedBlockIds={selectedBlockIds}
                isPickingColor={!!colorPickerCallback}
                onPickColor={handleColorPicked}
                isFinished={selectedImage.finished}
              />
            </div>
          </div>
        )}
      </div>

      {selectedBlockIds.size > 0 && (
        <EditableTextOptions
          imageCanvasRef={imageCanvasRef}
          blocks={selectedBlocks}
          commonValues={getCommonValues()}
          position={optionsPanelPosition}
          onDrag={handleOptionsPanelDrag}
          onUpdate={handleSelectedTextBlocksUpdate}
          onUpdateBlock={handleTextBlockChange}
          onDelete={() => {
            selectedBlockIds.forEach(id => handleTextBlockDelete(id));
            setSelectedBlockIds(new Set());
          }}
          onPickColor={handlePickColor}
        />
      )}
    </div>
  );
}

export default ImageTranslator;


function ImageTranslatorEmpty({
  isLoading,
  handleImageUpload
}: {
  isLoading: boolean;
  handleImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="bg-white p-8 rounded-lg shadow-md text-center">
      <label className="cursor-pointer inline-block">
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12">
          {isLoading ? (
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 mx-auto"></div>
          ) : (
            <>
              <p className="mt-2 text-sm text-gray-600">Click to upload an image</p>
            </>
          )}
        </div>
        <input
          type="file"
          className="hidden"
          accept="image/*"
          multiple
          onChange={handleImageUpload}
          disabled={isLoading}
        />
      </label>
    </div>
  )
}